import { useTranslation } from 'react-i18next';
import type { OfflineReadiness } from '@ghostscreen/contracts';
import { formatOfflineReadiness } from '../../lib/offline-readiness/formatOfflineReadiness';

type OfflineReadinessLineProps = {
  readiness: OfflineReadiness;
  /** Compact card footer vs manage-panel body. */
  density?: 'card' | 'panel';
};

/**
 * Operator-facing content offline readiness.
 * Separate from connection/sync pills — different axis.
 */
export function OfflineReadinessLine({
  readiness,
  density = 'card',
}: OfflineReadinessLineProps) {
  const { t } = useTranslation();
  const presentation = formatOfflineReadiness(readiness);
  const tone =
    readiness.percent === 100
      ? 'text-ok'
      : readiness.percent >= 80
        ? 'text-ink'
        : 'text-bad';

  if (density === 'panel') {
    return (
      <div
        className="rounded-[12px] border border-line bg-panel2 px-4 py-3"
        role="status"
        aria-label={presentation.label}
      >
        <p className={`m-0 text-sm font-semibold ${tone}`}>{presentation.summary}</p>
        {presentation.detail ? (
          <p className="m-0 mt-1 text-sm text-muted">{presentation.detail}</p>
        ) : (
          <p className="m-0 mt-1 text-sm text-muted">{t('offlineReadiness.assignedCanPlay')}</p>
        )}
      </div>
    );
  }

  return (
    <p
      className={`m-0 text-[12.5px] font-medium ${tone}`}
      title={presentation.label}
      aria-label={presentation.label}
    >
      {presentation.summary}
      {presentation.detail ? (
        <span className="font-normal text-muted"> — {presentation.detail}</span>
      ) : null}
    </p>
  );
}
